import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import DocumentStoreFileDetail from './DocumentStoreFileDetail';
import { PanelItem } from '@components/panels/PanelItems';
import { IdentityDocumentType } from '@services/identityVerification/IdentityVerification.types';
// eslint-disable-next-line camelcase
import { formatDateToDD_MMM_YYYY } from '@utils/misc/functions';

export type AdditionalData = {
    DateOfBirth?: string,
    State?: string,
    Nationality?: string,
    IssueDate?: string,
    Jurisdiction?: string,
    AddressLines?: string[],
    Suburb?: string,
    PostCode?: string,
    Country?: string
};

export type DocumentStoreRecordProps = {
    documentStoreRecordId: number,
    description?: string,
    documentType: string,
    documentNumber?: string,
    expiryDate?: Date,
    files?: any[],
    additionalData?: AdditionalData
};

export default function DocumentStoreRecord(props: DocumentStoreRecordProps) {
    const navigate = useNavigate();
    const { country, custId } = useParams<{ country: string; custId: string }>();
    const fieldStyle = { margin: '1vh 2vw' };
    const fieldLabelStyle = { bold: true };

    return (
        <>
            <div style={{ margin: '2vh 2vw', fontSize: '20px', fontWeight: 'bold' }}>
                {props.description}
            </div>

            <PanelItem header='Document Type' headerTextStyleCfg={fieldLabelStyle} value={props.documentType}
                itemStyleCfg={fieldStyle} />

            {
                props.documentNumber &&
                <PanelItem header='Document Number' headerTextStyleCfg={fieldLabelStyle} value={props.documentNumber}
                    itemStyleCfg={fieldStyle} />
            }

            {
                props.additionalData?.State &&
                <PanelItem
                    header='Licence State'
                    headerTextStyleCfg={fieldLabelStyle}
                    value={props.additionalData.State}
                    itemStyleCfg={fieldStyle}
                />
            }

            {
                props.additionalData?.IssueDate &&
                <PanelItem
                    header='Issue Date'
                    headerTextStyleCfg={fieldLabelStyle}
                    value={formatDateToDD_MMM_YYYY(props.additionalData.IssueDate)}
                    itemStyleCfg={fieldStyle}
                />
            }

            {
                props.additionalData?.Jurisdiction &&
                <PanelItem
                    header='Licence Class'
                    headerTextStyleCfg={fieldLabelStyle}
                    value={props.additionalData.Jurisdiction}
                    itemStyleCfg={fieldStyle}
                />
            }

            {
                props.expiryDate &&
                <PanelItem header='Expiry Date' headerTextStyleCfg={fieldLabelStyle}
                    value={props.expiryDate.toDateString()} itemStyleCfg={fieldStyle} />
            }

            {
                props.additionalData?.DateOfBirth &&
                <PanelItem header='Date of Birth' headerTextStyleCfg={fieldLabelStyle}
                    value={formatDateToDD_MMM_YYYY(props.additionalData.DateOfBirth)} itemStyleCfg={fieldStyle} />
            }

            {
                props.additionalData?.Nationality &&
                <PanelItem header='Nationality' headerTextStyleCfg={fieldLabelStyle}
                    value={props.additionalData.Nationality} itemStyleCfg={fieldStyle} />
            }

            {
                props.additionalData?.AddressLines &&
                <PanelItem header='Address' headerTextStyleCfg={fieldLabelStyle}
                    value={props.additionalData.AddressLines.join(', ')} itemStyleCfg={fieldStyle} />
            }

            {
                props.additionalData?.Suburb &&
                <PanelItem header='City' headerTextStyleCfg={fieldLabelStyle} value={props.additionalData.Suburb}
                    itemStyleCfg={fieldStyle} />
            }

            {
                props.additionalData?.PostCode &&
                <PanelItem header='Post Code' headerTextStyleCfg={fieldLabelStyle}
                    value={props.additionalData.PostCode} itemStyleCfg={fieldStyle} />
            }

            {
                props.files && props.files.length > 0 &&
                <div data-testid='documentStoreRecordFileListing'>
                    {
                        props.files.map(
                            (file: any, index: number) => (
                                <div style={{ margin: '1vh 2vw' }}
                                    key={`documentStoreFileDetail-div-${props.documentStoreRecordId}-${index}`}
                                >
                                    <DocumentStoreFileDetail
                                        documentStoreRecordId={props.documentStoreRecordId}
                                        s3Key={file['s3_key']}
                                        description={file['description']}
                                        originalFileName={file['original_file_name']}
                                        presignedDownloadURL={file['presigned_download_url']}
                                    />
                                </div>
                            )
                        )
                    }
                </div>
            }

            {
                country && custId && props.documentType === IdentityDocumentType.DRIVER_LICENCE &&
                <PanelItem
                    // We only want to show the value in a matching format, but an empty header renders 'HEADER-NOT-SET'
                    header=' '
                    value={
                        <a
                            href=''
                            onClick={(e) => {
                                e.preventDefault();
                                navigate(`/identity-verification/${country}/${custId}/update-licence`);
                            }}
                            data-testid='update-document-link'
                        >
                            Update Drivers Licence
                        </a>
                    }
                    itemStyleCfg={fieldStyle}
                />
            }
        </>
    )
}